/**
 * Google reCAPTCHA v3 helpers shared by every form that posts to the ECQ API.
 *
 * The backend answers `{"status":"fail","message":"Oops! Invalid reCAPTCHA"}`
 * whenever `recaptchaResponse` is absent, empty, malformed, REUSED, or older
 * than ~120 s — it never looks at the v3 score. So a token must be minted fresh
 * for every single POST and never resent.
 *
 * `getRecaptchaToken()` enforces that: it waits for the script injected in
 * app/layout.tsx, wraps `grecaptcha.ready()` in a real promise (the raw call
 * returns `undefined`, so awaiting it hides failures) and throws instead of
 * handing back an empty token — the guard V1 had as `if (token) { ... }`.
 * Note `JSON.stringify` drops keys whose value is `undefined`, so an unguarded
 * token silently posts a body with no `recaptchaResponse` at all.
 *
 * Every error thrown here mentions "reCAPTCHA", so a caller can route both a
 * thrown error and a parsed backend response through `isRecaptchaFailure()`
 * and show `RECAPTCHA_FAILED_MESSAGE` instead of the raw backend wording.
 */
import { getApiErrorMessage } from "./apiError";

/** Site key for e-cq.net — also used by the loader in app/layout.tsx. */
export const RECAPTCHA_SITE_KEY = "6LfpccUpAAAAAMNo0vXjKLVGeEYOF2vdu2jtweOC";

/** Friendly stand-in for the backend's raw "Oops! Invalid reCAPTCHA". */
export const RECAPTCHA_FAILED_MESSAGE =
  "We couldn't verify that you are human. Please reload the page and send your message again.";

/** How long to wait for the reCAPTCHA script / its `ready` callback. */
const READY_TIMEOUT_MS = 10_000;
const POLL_INTERVAL_MS = 100;

/**
 * Captcha-flavoured wording. Matches the current backend message
 * ("Oops! Invalid reCAPTCHA"), a reworded variant, a missing
 * `recaptchaResponse` validation error, and the errors thrown below.
 */
const CAPTCHA_FAILURE_PATTERN = /captcha/i;

/**
 * True when a parsed backend response (or an error thrown by
 * `getRecaptchaToken`) is a captcha rejection rather than a real validation
 * error. Uses the same message extraction as the text shown to users.
 */
export function isRecaptchaFailure(result: unknown): boolean {
  return CAPTCHA_FAILURE_PATTERN.test(getApiErrorMessage(result, ""));
}

/**
 * Resolve once the reCAPTCHA script has attached `window.grecaptcha`. The
 * script is `afterInteractive`, so a fast submit can land before it exists.
 */
function waitForGrecaptcha(): Promise<Window["grecaptcha"]> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("reCAPTCHA is only available in the browser."));
      return;
    }
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.grecaptcha) {
        window.clearInterval(timer);
        resolve(window.grecaptcha);
      } else if (Date.now() - startedAt >= READY_TIMEOUT_MS) {
        window.clearInterval(timer);
        reject(new Error("The reCAPTCHA script failed to load in time."));
      }
    }, POLL_INTERVAL_MS);
  });
}

/**
 * Reject if one of Google's promises never settles. Every step below is
 * time-boxed: a caller that awaits this runs its `finally`, so the submit
 * button always returns from "Sending..." instead of wedging there.
 */
function withTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error(message)),
      READY_TIMEOUT_MS
    );
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Mint a fresh reCAPTCHA v3 token. Rejects — never resolves with an empty
 * string — when the script is missing, `ready()` never fires, `execute()`
 * hangs, or Google hands back a falsy token, so the caller can surface the
 * failure and unwedge its submitting state.
 */
export async function getRecaptchaToken(action = "submit"): Promise<string> {
  const grecaptcha = await waitForGrecaptcha();

  // grecaptcha.ready() is callback-based and returns undefined: awaiting it
  // directly resolves immediately and swallows anything the callback does.
  await withTimeout(
    new Promise<void>((resolve) => {
      grecaptcha.ready(() => resolve());
    }),
    "reCAPTCHA did not finish initialising in time."
  );

  const token = await withTimeout(
    grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }),
    "reCAPTCHA did not return a token in time."
  );

  if (!token || !token.trim()) {
    throw new Error("reCAPTCHA returned an empty token.");
  }

  return token;
}
